'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { BookOpen, CreditCard, KeyRound } from 'lucide-react';

import { Button } from '#/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog';
import { completeOnboarding } from '#/actions/server/onboarding';
import { ROUTES } from '#/data/constants';

// 결제·카드 등록 화면에서는 안내가 흐름을 끊으므로 띄우지 않는다.
const SKIP_PATHS = [
  ROUTES.PRODUCT_CHECKOUT,
  ROUTES.PAYMENT,
  ROUTES.BILLING_CARD,
  ROUTES.LOGIN,
  ROUTES.REGISTER,
  ROUTES.LOGOUT
];

const STEPS = [
  {
    icon: KeyRound,
    title: 'API 키 확인하기',
    body: '가입하면 API 키가 바로 발급됩니다. 키 확인, 복사, 재발급과 사용량은 API KEY · 사용량 화면에서 봅니다.',
    href: ROUTES.API_KEY,
    cta: 'API 키 확인'
  },
  {
    icon: CreditCard,
    title: '구독 결제하기',
    body: '가입하면 50,000어절을 무료로 쓸 수 있습니다. 더 필요하면 상품 화면에서 구독을 시작하세요. 스탠다드 플랜은 1개월 무료 체험이 있습니다(1인 1회).',
    href: ROUTES.PRODUCT,
    cta: '상품 보기'
  },
  {
    icon: BookOpen,
    title: 'API 사용 시작하기',
    body: '받은 키를 api-key 헤더에 넣으면 형태소 분석과 맞춤법 교정을 호출할 수 있습니다. 사용법은 문서에 정리해 두었습니다.',
    href: ROUTES.DOCS,
    cta: '문서 보기'
  }
];

/**
 * 로그인 후 처음 한 번 보여 주는 시작 안내 (#297).
 *
 * 마쳤는지는 DB(users.onboarded_at)에 기록한다. 어느 브라우저에서 봤든 한 번만 뜬다.
 * 닫기, 나중에, 단계 버튼 어느 쪽이든 마친 것으로 친다.
 */
export function OnboardingDialog() {
  const pathname = usePathname();
  const [open, setOpen] = useState(true);

  const skipped = SKIP_PATHS.some((p) => pathname?.startsWith(p));

  const dismiss = () => {
    setOpen(false);
    // 기록이 실패해도 이번 화면에서는 닫힌다. 다음 로그인에 한 번 더 뜰 뿐이다.
    completeOnboarding().catch(() => {});
  };

  return (
    <Dialog open={open && !skipped} onOpenChange={(next) => !next && dismiss()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>바른AI에 오신 것을 환영합니다</DialogTitle>
          <DialogDescription>
            시작하기 전에 알아 두면 좋은 세 가지입니다.
          </DialogDescription>
        </DialogHeader>

        <ol className="grid gap-3">
          {STEPS.map(({ icon: Icon, title, body, href, cta }, i) => (
            <li
              key={href}
              className="flex items-start gap-3 rounded-lg border p-3"
            >
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="flex-1 text-sm">
                <p className="font-medium">
                  {i + 1}. {title}
                </p>
                <p className="mt-1 text-muted-foreground">{body}</p>
              </div>
              <Button asChild variant="outline" size="sm" onClick={dismiss}>
                <Link href={href}>{cta}</Link>
              </Button>
            </li>
          ))}
        </ol>

        <DialogFooter>
          <Button variant="ghost" onClick={dismiss}>
            나중에 볼게요
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
